import { useEffect } from "react";
import { getCalApi } from "@calcom/embed-react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { HeaderOptionsPopover } from "@/components/HeaderOptionsPopover";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Video, Star, Trophy } from "lucide-react";
import { useTranslation } from "react-i18next";
import {AutoTranslate} from "@/lib/autoTranslate.tsx"

export default function ShareSuccess() {
  const { t } = useTranslation();
  const autoTranslateText = t('autoTranslate', { returnObjects: true });
  
  useEffect(() => {
    (async function () {
      const cal = await getCalApi({"namespace":"testimony"});
      cal("ui", {"theme":"light","cssVarsPerTheme":{"light":{"cal-brand":"#ff339c"},"dark":{"cal-brand":"#ff339c"}},"hideEventTypeDetails":false,"layout":"month_view"});
    })();
  }, []);

  const handleTrustpilotClick = () => {
    window.open('https://www.trustpilot.com/review/wefind.space', '_blank');
  };
  
  // Reactive translation variables
  const shareYourSuccess = autoTranslateText["Share Your Success"] || AutoTranslate("Share Your Success");
  const shareStory = autoTranslateText["Share your story in a recorded video call and get featured on our Testimonial Wall."] || AutoTranslate("Share your story in a recorded video call and get featured on our Testimonial Wall.");
  const beSpotlight = autoTranslateText["Be Our Spotlight"] || AutoTranslate("Be Our Spotlight");
  const scheduleCall = autoTranslateText["Schedule Your Call"] || AutoTranslate("Schedule Your Call");
  const leaveReview = autoTranslateText["Leave Us a Review"] || AutoTranslate("Leave Us a Review");
  const preferWrite = autoTranslateText["Prefer to write it? Share your experience publicly on Trustpilot to help others discover us."] || AutoTranslate("Prefer to write it? Share your experience publicly on Trustpilot to help others discover us.");
  const writeOnTrustpilot = autoTranslateText["Write on Trustpilot"] || AutoTranslate("Write on Trustpilot");

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-gradient-main">
        <AppSidebar />

        <main className="flex-1 flex flex-col relative z-10">
          <header className="flex items-center justify-between p-4 border-b border-border bg-card/80 backdrop-blur-sm">
            <div className="flex items-center gap-4">
              <SidebarTrigger className="hover:bg-primary-light" />
              <div className="flex items-center gap-2">
                <Trophy className="h-6 w-6 text-primary" />
                <h1 className="text-xl font-bold text-foreground">{shareYourSuccess}</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <HeaderOptionsPopover />
            </div>
          </header>

          <div className="flex-1 p-6 max-w-6xl mx-auto w-full">
            {/* Hero Section */}
            <div className="text-center mb-12 space-y-4">
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-primary/10 rounded-full">
                  <Trophy className="h-16 w-16 text-primary" />
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                {autoTranslateText["Share Your Success!"] || AutoTranslate("Share Your Success!")}
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {autoTranslateText["Your journey inspires others. Choose how you'd like to celebrate your achievement and help future users land their dream jobs."] || AutoTranslate(`Your journey inspires others. Choose how you'd like to celebrate your achievement and help future users land their dream jobs.`)}
              </p>
            </div>

            {/* Two-Option Cards */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Option 1: Video Testimonial */}
              <Card className="bg-card/80 backdrop-blur-sm border-card-border hover:shadow-lg transition-all duration-300 hover:scale-105">
                <CardHeader>
                  <div className="flex justify-center mb-4">
                    <div className="p-3 bg-primary/10 rounded-full">
                      <Video className="h-8 w-8 text-primary" />
                    </div>
                  </div>
                  <CardTitle className="text-2xl text-center">{beSpotlight}</CardTitle>
                  <CardDescription>
                    {shareStory}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex justify-center">
                  <Button
                    data-cal-namespace="testimony"
                    data-cal-link="f-i-n-d/testimony"
                    data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true","theme":"light"}'
                    className="w-full max-w-xs"
                  >
                    <Video className="h-4 w-4 mr-2" />
                    {scheduleCall}
                  </Button>
                </CardContent>
              </Card>

              {/* Option 2: Trustpilot Review */}
              <Card className="bg-card/80 backdrop-blur-sm border-card-border hover:shadow-lg transition-all duration-300 hover:scale-105">
                <CardHeader>
                  <div className="flex justify-center mb-4">
                    <div className="p-3 bg-primary/10 rounded-full">
                      <Star className="h-8 w-8 text-primary" />
                    </div>
                  </div>
                  <CardTitle className="text-2xl text-center">{leaveReview}</CardTitle>
                  <CardDescription>
                    {preferWrite}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex justify-center">
                  <Button
                    onClick={handleTrustpilotClick}
                    className="w-full max-w-xs"
                    variant="outline"
                  >
                    <Star className="h-4 w-4 mr-2" />
                    {writeOnTrustpilot}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>

          <Footer />
        </main>
      </div>
    </SidebarProvider>
  );
}
